import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  MessageCircle,
  Phone,
  Star,
  Gift,
  Clock,
  Users,
  Sparkles,
  Heart,
  Zap,
  CheckCircle,
  ArrowRight,
  Calendar,
  Percent,
  CreditCard,
  Shield,
  Award
} from 'lucide-react';

const Packages = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <Badge className="gradient-primary text-white px-4 py-2 text-lg font-semibold">
                <Gift className="h-5 w-5 mr-2" />
                Premium Packages & Pricing
              </Badge>
            </div>
            <h1 className="text-hero text-foreground">Wellness Packages & Pricing</h1>
            <p className="text-xl text-primary font-medium">Professional treatments with flexible payment options</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Discover our comprehensive range of wellness packages designed to help you achieve your goals. 
              All services accept medical aid for consultations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 animate-glow w-full sm:w-auto">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Book Your Package
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                  <Phone className="h-4 w-4 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Aid Notice */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <Card className="shadow-elegant border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="p-8">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-green-800">Medical Aid Accepted</h2>
                </div>
                <p className="text-lg text-green-700 max-w-3xl mx-auto">
                  <strong>All medical aid providers are accepted for consultations.</strong> We work with all major medical aid schemes 
                  to make your wellness journey more accessible and affordable.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-4">
                  <Badge className="bg-green-600 text-white px-4 py-2">Discovery Health</Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2">Momentum</Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2">Bonitas</Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2">Medihelp</Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2">Gems</Badge>
                  <Badge className="bg-green-600 text-white px-4 py-2">All Medical Aids</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Combo Packages */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Combo Treatment Packages</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Targeted combo treatments for specific body areas at special pricing
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Combo 1 */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-blue-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-blue-600 text-white">
                  <Zap className="h-3 w-3 mr-1" />
                  Combo 1
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Arms, Waist, Belly</h3>
                      <p className="text-primary font-medium">Targeted upper body sculpting</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Treatment Areas:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Arms Sculpting</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Waist Contouring</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Belly Fat Reduction</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">1 Session:</span>
                      <span className="text-xl font-bold text-primary">R1,500</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-100 to-indigo-100 p-4 rounded-lg border-2 border-blue-300">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">2 Sessions:</span>
                      <span className="text-xl font-bold text-primary">R2,900</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Save R100!</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Combo 1
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Combo 2 */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-purple-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-purple-600 text-white">
                  <Award className="h-3 w-3 mr-1" />
                  Combo 2
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Heart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Arms, Waist, Belly, Thighs</h3>
                      <p className="text-primary font-medium">Complete body transformation</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Treatment Areas:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Arms Sculpting</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Waist Contouring</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Belly Fat Reduction</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">Thigh Sculpting</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">1 Session:</span>
                      <span className="text-xl font-bold text-primary">R2,000</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-lg border-2 border-purple-300">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">2 Sessions:</span>
                      <span className="text-xl font-bold text-primary">R3,900</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Save R100!</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Combo 2
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Premium Packages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Premium Wellness Packages</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive treatment packages combining multiple services for maximum results and value
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* R3500 Package */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-primary/20 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-blue-600 text-white">
                  <Star className="h-3 w-3 mr-1" />
                  Starter Package
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Starter Wellness Package</h3>
                      <p className="text-primary font-medium">Complete body transformation starter</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Package Includes:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">3x Cavitation Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">3x Lipo Laser Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">3x Vacuum Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">3x RF (Radio Frequency) Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">1x Wood Therapy Session</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">20x Slimming Injections</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                  <div className="text-center">
                    <span className="text-3xl font-bold text-primary">R3,500</span>
                    <p className="text-sm text-muted-foreground">Complete Package</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Starter Package
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* R4500 Package */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-green-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-green-600 text-white">
                  <Award className="h-3 w-3 mr-1" />
                  Most Popular
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Heart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Enhanced Wellness Package</h3>
                      <p className="text-primary font-medium">Advanced body sculpting & wellness</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Package Includes:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">4x Cavitation Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">4x Lipo Laser Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">4x Vacuum Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">4x RF (Radio Frequency) Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">2x Wood Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">1x Lymphatic Drainage Massage</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">30x Slimming Injections</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg">
                  <div className="text-center">
                    <span className="text-3xl font-bold text-primary">R4,500</span>
                    <p className="text-sm text-muted-foreground">Enhanced Package</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Enhanced Package
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* R6500 Package */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-purple-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-purple-600 text-white">
                  <Zap className="h-3 w-3 mr-1" />
                  Premium
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Zap className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Premium Transformation Package</h3>
                      <p className="text-primary font-medium">Complete wellness transformation</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Package Includes:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x Cavitation Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x Lipo Laser Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x Vacuum Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x RF (Radio Frequency) Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">4x Wood Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">1x Weight Loss Drip</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">2x Lymphatic Drainage Massage</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">30x Slimming Injections</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg">
                  <div className="text-center">
                    <span className="text-3xl font-bold text-primary">R6,500</span>
                    <p className="text-sm text-muted-foreground">Premium Package</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Premium Package
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* R8000 Package */}
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-gold-200 relative overflow-hidden bg-gradient-to-br from-yellow-50 to-orange-50">
              <div className="absolute top-4 right-4">
                <Badge className="bg-yellow-600 text-white">
                  <Award className="h-3 w-3 mr-1" />
                  Ultimate
                </Badge>
              </div>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                      <Award className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Ultimate Wellness Package</h3>
                      <p className="text-primary font-medium">Complete luxury transformation experience</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">Package Includes:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">2x Weight Loss Drips</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">60x Slimming Injections</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x Vacuum Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">6x RF (Radio Frequency) Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">5x Wood Therapy Sessions</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-muted-foreground">2x Lymphatic Drainage Massage</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-lg border-2 border-yellow-300">
                  <div className="text-center">
                    <span className="text-3xl font-bold text-primary">R8,000</span>
                    <p className="text-sm text-muted-foreground">Ultimate Package</p>
                  </div>
                </div>
                
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Ultimate Package
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Individual Services */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Individual Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Prefer individual treatments? Choose from our comprehensive range of single services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Body Treatments */}
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Body Treatments
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Lipo Laser (1 session)</span>
                    <span className="font-semibold">R400</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Lipo Laser (2 sessions)</span>
                    <span className="font-semibold">R600</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cavitation (1 session)</span>
                    <span className="font-semibold">R400</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cavitation (2 sessions)</span>
                    <span className="font-semibold">R700</span>
                  </div>
                  <div className="flex justify-between">
                    <span>RF (1 session)</span>
                    <span className="font-semibold">R400</span>
                  </div>
                  <div className="flex justify-between">
                    <span>RF (2 sessions)</span>
                    <span className="font-semibold">R700</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Vacuum (1 session)</span>
                    <span className="font-semibold">R400</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specialized Treatments */}
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Specialized Treatments
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Wood Therapy (1 session)</span>
                    <span className="font-semibold">R550</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Wood Therapy (2 sessions)</span>
                    <span className="font-semibold">R1,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Butt/Breast (1 session)</span>
                    <span className="font-semibold">R550</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Butt/Breast (2 sessions)</span>
                    <span className="font-semibold">R950</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Complete Injection Packages */}
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Slimming Injections
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>10 Injections</span>
                    <span className="font-semibold">R550</span>
                  </div>
                  <div className="flex justify-between">
                    <span>20 Injections</span>
                    <span className="font-semibold">R1,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>30 Injections</span>
                    <span className="font-semibold">R1,500</span>
                  </div>
                  <div className="flex justify-between">
                    <span>40 Injections</span>
                    <span className="font-semibold">R1,900</span>
                  </div>
                  <div className="flex justify-between">
                    <span>50 Injections</span>
                    <span className="font-semibold">R2,400</span>
                  </div>
                  <div className="flex justify-between">
                    <span>60 Injections</span>
                    <span className="font-semibold">R2,900</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Complete Glow Injections */}
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Glow Injections
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>1x Glow</span>
                    <span className="font-semibold">R150</span>
                  </div>
                  <div className="flex justify-between">
                    <span>2x Glow</span>
                    <span className="font-semibold">R200</span>
                  </div>
                  <div className="flex justify-between">
                    <span>3x Glow</span>
                    <span className="font-semibold">R350</span>
                  </div>
                  <div className="flex justify-between">
                    <span>4x Glow</span>
                    <span className="font-semibold">R400</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Complete Glutathione Injections */}
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Glutathione Injections
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>1x Glutathione</span>
                    <span className="font-semibold">R150</span>
                  </div>
                  <div className="flex justify-between">
                    <span>2x Glutathione</span>
                    <span className="font-semibold">R250</span>
                  </div>
                  <div className="flex justify-between">
                    <span>3x Glutathione</span>
                    <span className="font-semibold">R350</span>
                  </div>
                  <div className="flex justify-between">
                    <span>4x Glutathione</span>
                    <span className="font-semibold">R400</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* IV Drips */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">IV Drip Therapy</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Boost your wellness from within with our premium IV drip treatments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Popular Drips</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Glow Drip</span>
                    <span className="font-semibold text-primary">R800</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Weight Loss Drip</span>
                    <span className="font-semibold text-primary">R1,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Energy Drip</span>
                    <span className="font-semibold text-primary">R750</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cocktail Drip</span>
                    <span className="font-semibold text-primary">R800</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Beauty & Anti-Aging</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Advanced Skin Glow</span>
                    <span className="font-semibold text-primary">R1,300</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Skin Whitening</span>
                    <span className="font-semibold text-primary">R1,800</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Anti-Aging Drip</span>
                    <span className="font-semibold text-primary">R900</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Glutathione Drip</span>
                    <span className="font-semibold text-primary">R600</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Health & Wellness</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Immune Booster</span>
                    <span className="font-semibold text-primary">R800</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Vitamin C Drip</span>
                    <span className="font-semibold text-primary">R500</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Vitamin B12 Drip</span>
                    <span className="font-semibold text-primary">R500</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Babalas (Hangover)</span>
                    <span className="font-semibold text-primary">R900</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Massage Services - Updated with new services */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Therapeutic Massage Services</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Relax, rejuvenate, and restore with our professional massage treatments
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Relaxation Massages
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Back, Neck & Shoulders</span>
                      <p className="text-sm text-muted-foreground">30 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R300</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Full Body Massage</span>
                      <p className="text-sm text-muted-foreground">60 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R400</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Swedish Massage</span>
                      <p className="text-sm text-muted-foreground">30 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R250</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Swedish Massage</span>
                      <p className="text-sm text-muted-foreground">60 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R400</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Baby Massage</span>
                      <p className="text-sm text-muted-foreground">Gentle & nurturing</p>
                    </div>
                    <span className="font-semibold text-primary">R250</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Specialized Massages
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Lymphatic Drainage</span>
                      <p className="text-sm text-muted-foreground">60 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R450</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Full Body + Reflexology</span>
                      <p className="text-sm text-muted-foreground">45 minutes</p>
                    </div>
                    <span className="font-semibold text-primary">R500</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Foot Massage</span>
                      <p className="text-sm text-muted-foreground">Soak, exfoliate, scrub</p>
                    </div>
                    <span className="font-semibold text-primary">R250</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Full Back + Hot Towel</span>
                      <p className="text-sm text-muted-foreground">Therapeutic</p>
                    </div>
                    <span className="font-semibold text-primary">R250</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">Full Body + Hot Stone</span>
                      <p className="text-sm text-muted-foreground">Premium therapy</p>
                    </div>
                    <span className="font-semibold text-primary">R450</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Special Event Packages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Wedding & Event Packages</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Special packages for weddings and events - perfect for groups of 5 people
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <Badge className="bg-gray-600 text-white mb-4">Silver Package</Badge>
                  <h3 className="text-xl font-semibold text-foreground">Silver Wedding Package</h3>
                  <p className="text-sm text-muted-foreground">For 5 people</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">2x Glow Injections (Bride/Host)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">1x Glow Injection (4 People)</span>
                  </div>
                </div>
                <div className="text-center pt-4">
                  <span className="text-2xl font-bold text-primary">R750</span>
                </div>
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white">
                    Book Silver Package
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-yellow-300">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <Badge className="bg-yellow-600 text-white mb-4">Gold Package</Badge>
                  <h3 className="text-xl font-semibold text-foreground">Gold Wedding Package</h3>
                  <p className="text-sm text-muted-foreground">For 5 people</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">1x Glow Drip (Bride/Host)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">1x Glow Injection (4 People)</span>
                  </div>
                </div>
                <div className="text-center pt-4">
                  <span className="text-2xl font-bold text-primary">R1,250</span>
                </div>
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white">
                    Book Gold Package
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-elegant hover:shadow-lg transition-all duration-300 border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <Badge className="bg-purple-600 text-white mb-4">Diamond Package</Badge>
                  <h3 className="text-xl font-semibold text-foreground">Diamond Wedding Package</h3>
                  <p className="text-sm text-muted-foreground">For 5 people</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">5x Glow Drips (All 5 People)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-sm">Premium treatment for everyone</span>
                  </div>
                </div>
                <div className="text-center pt-4">
                  <span className="text-2xl font-bold text-primary">R3,800</span>
                </div>
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full gradient-primary text-white">
                    Book Diamond Package
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Custom Packages */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <Card className="shadow-elegant border-2 border-primary/20 bg-gradient-to-r from-pink-50 to-purple-50">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">Custom Packages Available</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Want to create your own combination? We offer custom packages tailored to your specific needs and goals. 
                  Mix and match any of our services to create the perfect wellness plan for you.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Create Custom Package
                    </Button>
                  </a>
                  <a href="tel:0695523598">
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                      <Phone className="h-4 w-4 mr-2" />
                      Discuss Options
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Terms & Medical Aid Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-section text-foreground">Payment & Medical Aid Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="shadow-soft border-0 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-green-600" />
                    Medical Aid Accepted
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• All medical aid providers accepted for consultations</li>
                    <li>• Discovery Health, Momentum, Bonitas, Medihelp</li>
                    <li>• Gems, Bestmed, and all other medical aids</li>
                    <li>• Consultation fees may be claimed back</li>
                    <li>• Bring your medical aid card for verification</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="shadow-soft border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Package Benefits
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• All packages include free consultation</li>
                    <li>• Flexible scheduling within validity period</li>
                    <li>• Progress tracking and monitoring included</li>
                    <li>• Professional aftercare guidance</li>
                    <li>• Secured parking available</li>
                    <li>• Custom packages available on request</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Start Your Wellness Journey?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Choose from our premium packages or create a custom plan. Medical aid accepted for consultations.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp to Book
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Packages;